// ブラウザのプラットフォーム層の入口（メインスレッド）。**配線だけを持つ** —
// 画面の組み立ては `screen.ts`、状態から決まる見え方は `app-state.ts`、文言は `failure-notice.ts`。
// 音声処理そのものはコア（`client/core` の WASM）が AudioWorklet の中で行う。
//
// **流れ**（ADR-0023・画面仕様 §1・§5）
//   ① ページを開いた時点で接続し、覚えている所属グループのチャンネルと在室者を出す
//   ② 利用者が行を選んで「入室」を押す（**音を出すのは操作が起点でなければならない**）
//   ③ 入室応答（`joined`）で設定が届いてから音声を用意する（コーデックとフレーム長が
//      チャンネルごとに違うため、先には作れない）
//
// **一覧は押し出しで新しくなる**（サーバが変化のたびに `rooms` を送り直す。ADR-0023 §1-2）。
// 受け取ったら描き直すだけでよく、要求の応答と押し出しを区別しない。

import { Audio } from "./audio.ts";
import {
  canCreate,
  canJoin,
  canLeave,
  canRetry,
  channelRows,
  keepSelection,
  personRows,
  type Phase,
} from "./app-state.ts";
import type { AudioProfile, Participant, RoomInfo, ServerMessage } from "./control.ts";
import { connectionNotice, detailLine, rejectionNotice, SUPPORTED_BROWSERS } from "./failure-notice.ts";
import {
  addGroup,
  buildGroupInviteUrl,
  buildInviteUrl,
  parseInviteTarget,
  parseRoomCode,
  rememberChannelName,
  sanitizeChannelNames,
  sanitizeGroups,
  toRoomId,
  validateDisplayName,
} from "./invite.ts";
import { buildLogText, localTimestamp, logFileName, MAX_RECORDED_LINES, type LogMeta } from "./log-file.ts";
import { autoLowpassHz } from "./profile-view.ts";
import { classifyStage, flushQueued, networkKind, portOf, reportAttempt } from "./reachability.ts";
import { causeOfStage, POLICY, Reconnect } from "./reconnect.ts";
import { Screen, type ScreenHandlers } from "./screen.ts";
import { Transport } from "./transport.ts";
import { ScreenKeepAwake } from "./wake-lock.ts";
// **検証用。** 設定の画面に折りたたんで置く（利用者に見せない位置）。板 #90 で外す
import { mountDiagnostics } from "./diagnostics/index.ts";

/** アプリのバージョン。ビルド時に埋まる（埋まっていなければ `dev`）。 */
const APP_VERSION: string = typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "dev";

/**
 * 接続先は**埋め込む**（利用者に手入力させない。画面仕様 §6-2）。
 * **通話の公開ポートは UDP 443**（ADR-0014 §1-1）。
 */
const SERVER_URL = `https://${location.hostname || "localhost"}:443`;

/** 覚えておくもの（`localStorage`。**サーバは所属を知らない**。ADR-0019 §1-7）。 */
const KEY = {
  name: "telephone.name",
  groups: "telephone.groups",
  channels: "telephone.channels",
  groupNames: "telephone.groups.names",
  recent: "telephone.recent",
  audio: "telephone.audio",
} as const;

/** 前にいた場所を覚えておく数（板 #220）。 */
const RECENT_MAX = 8;

function load<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

function save(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // 保存できなくても動く（iOS は 7 日で消す。ADR-0019 §7 のリスク 1）
  }
}

// ---- 覚えているもの --------------------------------------------------------

let myName = typeof load<unknown>(KEY.name, "") === "string" ? (load(KEY.name, "") as string) : "";
let groups = sanitizeGroups(load<unknown>(KEY.groups, []));
let channelNames = sanitizeChannelNames(load<unknown>(KEY.channels, {}));
/**
 * 覚えているグループの表示名（コード → 名前）。
 *
 * **所属（どのグループに居るか）はサーバが知らない**ので端末が持つ（ADR-0019 §1-7）。
 * **名前は `rooms` で届く**（2026-10-29）ので、自分で作ったグループでも招待された
 * グループでも埋まる。**繋がる前に出すために覚えておく。**
 */
let groupNames = sanitizeChannelNames(load<unknown>(KEY.groupNames, {}));
let recent = sanitizeGroups(load<unknown>(KEY.recent, [])).slice(0, RECENT_MAX);
const audioPrefs = {
  master: 0,
  send: 0,
  highpass: 300,
  /** `null` は「チャンネルの上限いっぱい」（チャンネルごとに最大が違う）。 */
  lowpass: null as number | null,
  ...load<Record<string, number | null>>(KEY.audio, {}),
};

// ---- いまの状態 ------------------------------------------------------------

interface Call {
  transport: Transport;
  audio: Audio | null;
  profile: AudioProfile | null;
  self: number | null;
  roster: Map<number, Participant>;
  /** いま喋っている人（サーバの `speaking`。自分も入る）。 */
  speaking: Set<number>;
  /** 入室しているチャンネルの識別子。未入室なら空。 */
  room: string;
  startedAt: number;
}

let call: Call | null = null;
let phase: Phase = { kind: "connecting" };
/** いま見ているグループ（空なら常設の部屋）。 */
let group = groups[0] ?? "";
/** サーバから届いたチャンネル（要求の応答と押し出しの両方がここに入る）。 */
let rooms: RoomInfo[] = [];
let selected = "";
/** 招待 URL の行き先（`グループ/チャンネル`）。 */
let invitedRoom = "";
/** 参加者ごとの受信音量。**退室で捨てる**（参加者 ID は入室ごとに払い出される）。 */
const personGainDb = new Map<number, number>();
/** 自分の操作で閉じているあいだ（切れた警告を二重に出さないため）。 */
let leavingByUs = false;
const reconnect = new Reconnect();
let reconnectTimer: number | null = null;
let rejoinCount = 0;

// ---- ログ（画面には出さず、保存とコンソールに残す） ----------------------
// 画面のログ欄は本番では作らない（利用者に見せるのは §7 の案内だけ）。
// **保存ログは当面残す**（板 #82 でリリース前に外す）。

const recordedLines: string[] = [];

function log(level: "info" | "warn" | "error", text: string): void {
  const mark = level === "error" ? "❌" : level === "warn" ? "⚠" : "・";
  recordedLines.push(`${localTimestamp(new Date())} ${mark} ${text}`);
  while (recordedLines.length > MAX_RECORDED_LINES) recordedLines.shift();
  if (level === "error") console.error(text);
  else if (level === "warn") console.warn(text);
  else console.info(text);
}

// ---- 画面 ------------------------------------------------------------------

const handlers: ScreenHandlers = {
  onSelectChannel(id) {
    selected = id;
    render();
  },
  onJoin() {
    void join(selected);
  },
  onCreate(displayName) {
    const bad = validateDisplayName(displayName);
    screen.setCreateError(bad);
    if (bad !== null) return;
    void create(displayName);
  },
  onCopyGroupInvite() {
    void copyGroupInvite();
  },
  onCreateGroup(displayName) {
    const bad = validateDisplayName(displayName);
    screen.setCreateError(bad);
    if (bad !== null) return;
    void createGroup(displayName);
  },
  onLeave() {
    void leave();
  },
  onRetry() {
    cancelReconnect();
    void connect();
  },
  onSelectGroup(next) {
    group = next;
    selected = "";
    rooms = [];
    render();
    // **見張るのは画面に出している 1 グループだけ**（ADR-0023 §5 の未決 2 の決着）。
    // 送り直すとサーバ側の見張りが差し替わる
    void call?.transport.listRooms(group || undefined, true);
  },
  onForget(id) {
    recent = recent.filter((r) => r !== id);
    save(KEY.recent, recent);
    render();
  },
  onName(name) {
    myName = name;
    save(KEY.name, myName);
  },
  onMute(muted) {
    call?.audio?.setMuted(muted);
    screen.setMuted(muted);
    render();
  },
  onMasterGainDb(db) {
    audioPrefs.master = db;
    call?.audio?.setMasterGainDb(db);
    save(KEY.audio, audioPrefs);
  },
  onSendGainDb(db) {
    audioPrefs.send = db;
    call?.audio?.setGainDb(db);
    save(KEY.audio, audioPrefs);
  },
  onPersonGainDb(id, db, commit) {
    personGainDb.set(id, db);
    // **MCU は合成 1 本しか届かない**ので、1 人ずつの音量はサーバが混ぜるときに掛ける。
    // 制御ストリームが詰まるので**離したときに 1 通だけ**送る（画面仕様 §3）
    if (commit) {
      call?.transport.sendSetMixGain(id, db);
      log("info", `受信音量: #${id} を ${db} dB にしました`);
    }
  },
  onBandHz(highpassHz, lowpassHz, commit) {
    audioPrefs.highpass = highpassHz;
    audioPrefs.lowpass = lowpassHz;
    call?.audio?.setHighpassHz(highpassHz);
    call?.audio?.setLowpassHz(lowpassHz);
    if (commit) {
      save(KEY.audio, audioPrefs);
      log("info", `送る音の範囲: ${highpassHz}〜${lowpassHz} Hz にしました`);
    }
  },
  onCopyInvite() {
    void copyInvite();
  },
  onSaveLog() {
    saveLog();
  },
};

const screen = new Screen(document.getElementById("app")!, handlers);
const keepAwake = new ScreenKeepAwake(navigator.wakeLock, (level, message) => {
  log(level, message);
  // **取れなかったことは 1 回だけ知らせる**（iOS Safari 18.4 より前は取れない。画面仕様 §4）
  if (level === "warn") screen.setNotice({ head: message });
});

/** いま出している画面と、ボタンの可否・一覧・参加者をまとめて描き直す。 */
function render(): void {
  const rows = channelRows({ rooms, group, invitedRoom, recent, names: channelNames });
  // **押し出しで選んでいた行が消えたら選択を外す**（知らせは出さない。DESIGN-0006 §4-2）
  selected = keepSelection(rows, selected);
  screen.setStatus(phase);
  screen.setGroups(groups, group, groupNames);
  screen.setChannels(rows, selected);
  screen.setButtons({
    join: canJoin(phase, selected),
    create: canCreate(phase) && group !== "",
    // **グループは誰でも作れる**（ADR-0024・2026-10-21 のユーザー判断）。
    // **所属が 1 つも無い端末の入口でもある**（招待 URL が無いと何も作れなかった＝同 §2-1）
    createGroup: canCreate(phase),
    // **招待できるのは選んでいるグループ。** 繋がっていなくても作れる（URL は端末で組める）
    groupInvite: group !== "",
    leave: canLeave(phase),
    retry: canRetry(phase),
    mute: phase.kind === "in-call" && call?.audio !== null && call?.audio?.listenOnly === false,
  });
  if (call) {
    screen.setPeople(
      personRows({
        roster: [...call.roster.values()],
        self: call.self,
        speaking: call.speaking,
        muted: call.audio?.isMuted ?? false,
        mutedOthers: mutedOthers(),
        listenOnly: call.audio?.listenOnly ?? false,
      }),
      personGainDb,
    );
  }
}

/** 名簿に載っているミュート中の相手（S1。申告が無い人は入れない）。 */
function mutedOthers(): Set<number> {
  const out = new Set<number>();
  for (const p of call?.roster.values() ?? []) if (p.muted === true) out.add(p.id);
  return out;
}

// ---- 接続と入室 ------------------------------------------------------------

/** ページを開いた時点で繋ぐ（**押させない**。ADR-0023 §1-1・DESIGN-0006 §3-3）。 */
async function connect(): Promise<void> {
  if (call) return;
  phase = { kind: "connecting" };
  screen.setNotice(null);
  render();
  const startedAt = performance.now();
  try {
    const pending: Call = {
      transport: null as unknown as Transport,
      audio: null,
      profile: null,
      self: null,
      roster: new Map(),
      speaking: new Set(),
      room: "",
      startedAt: Date.now(),
    };
    const transport = await Transport.connect(
      { url: SERVER_URL },
      {
        onMessage: onServerMessage,
        // 音声の用意が終わる前に届いたものは捨てる（ジッタバッファが溜め直す）
        onDatagram: (bytes) => pending.audio?.onDatagram(bytes),
        onClose: (reason) => onClosed(reason),
      },
    );
    pending.transport = transport;
    call = pending;
    phase = { kind: "ready" };
    // 到達確認（ADR-0014 §1-2）。**繋がった場合も送る**ので母数が分かる
    void reportAttempt(
      { route: "wt", port: portOf(SERVER_URL), ok: true, elapsedMs: performance.now() - startedAt },
      { appVersion: APP_VERSION },
    );
    // **見張りを頼む**（変化したらサーバが `rooms` を押し出す。ADR-0023 §1-2）。
    // サーバ側が未実装のあいだは**普通の一覧の要求として扱われる**（項目が無視される）
    await transport.listRooms(group || undefined, true);
    render();
  } catch (e) {
    const stage = classifyStage(e);
    void reportAttempt(
      { route: "wt", port: portOf(SERVER_URL), ok: false, stage, elapsedMs: performance.now() - startedAt },
      { appVersion: APP_VERSION },
    );
    log("error", `接続できませんでした（${stage}）: ${String(e)}`);
    showNotice(connectionNotice(stage));
    planReconnect(causeOfStage(stage), 0);
  }
}

/** 入室する。**覚えている表示名を添える**と、畳まれていたチャンネルが復活する（ADR-0022 §1-4）。 */
async function join(room: string): Promise<void> {
  const c = call;
  if (!c || room === "" || phase.kind !== "ready") return;
  if (myName === "") {
    screen.setNotice({ head: "名前を入れてください" });
    return;
  }
  pendingAction = "join";
  try {
    await c.transport.join(room, myName, {
      ...(channelNames[room] ? { room_display_name: channelNames[room] } : {}),
      app_version: APP_VERSION,
      net_kind: netKind(),
      rejoin_count: rejoinCount,
    });
    c.room = room;
  } catch (e) {
    pendingAction = null;
    log("error", `入室できませんでした（${room}）: ${String(e)}`);
    showNotice({ head: "入室できませんでした", action: "もう一度試してください" });
  }
}

/** 「新しく作る」。識別子はサーバが採番して `channel_created` で返す（ADR-0022 §1-3）。 */
async function create(displayName: string): Promise<void> {
  const c = call;
  if (!c || group === "" || phase.kind !== "ready") return;
  pendingAction = "channel";
  try {
    await c.transport.createChannel(group, displayName);
  } catch (e) {
    pendingAction = null;
    log("error", `チャンネルを作れませんでした: ${String(e)}`);
    showNotice({ head: "チャンネルを作れませんでした", action: "もう一度試してください" });
  }
}

/**
 * **新しいグループを作る**（ADR-0024）。コードはサーバが採番し、`channel_created` で返る。
 *
 * 入れる名前は 1 つで、**グループと最初のチャンネルの両方の表示名**になる。
 */
async function createGroup(displayName: string): Promise<void> {
  const c = call;
  if (!c || phase.kind !== "ready") return;
  pendingGroupName = displayName;
  pendingAction = "group";
  try {
    await c.transport.createGroup(displayName);
  } catch (e) {
    pendingGroupName = "";
    pendingAction = null;
    log("error", `グループを作れませんでした: ${String(e)}`);
    showNotice({ head: "グループを作れませんでした", action: "もう一度試してください" });
  }
}

/** 作っているグループの名前（`channel_created` が返ってきたときに覚えるため）。 */
let pendingGroupName = "";
/**
 * **いまサーバに頼んでいること**（断られたときの文言と、画面を止めるかの判断に使う）。
 *
 * **作成の失敗で画面を止めてはいけない** — サーバは作成を断っても接続を閉じない
 * （`server/common/src/session.rs` の `close: false`）ので、入室も作り直しも続けられる。
 */
let pendingAction: "join" | "channel" | "group" | null = null;

/** 回線の種別（粗い 3 区分。サーバのログで追う手掛かり）。 */
function netKind(): "wifi" | "cellular" | "other" {
  const kind = networkKind((navigator as { connection?: unknown }).connection);
  return kind === "wifi" || kind === "cellular" ? kind : "other";
}

function onServerMessage(m: ServerMessage): void {
  const c = call;
  if (!c) return;
  switch (m.type) {
    case "rooms":
      // **要求の応答と押し出しを区別しない**（ADR-0023 §1-3。届いたら描き直すだけ）
      rooms = m.rooms;
      for (const r of m.rooms) channelNames = rememberChannelName(channelNames, r.id, r.display_name);
      save(KEY.channels, channelNames);
      // **グループの表示名はこの電文でしか届かない**（2026-10-29）。招待 URL には
      // コードしか入っていない（ADR-0021）ので、**招待された端末はこれが無いと
      // チップにコードを出すしかない**。覚える先は**電文の `group`** で、
      // いま選んでいる `group` ではない（押し出しは 0.5 秒遅れて届くので、
      // 切り替えた直後に前のグループの写しが来ると取り違える）
      if (m.group !== undefined && m.group_display_name !== undefined) {
        const next = rememberChannelName(groupNames, m.group, m.group_display_name);
        // 押し出しは変化のたびに来るので、**変わったときだけ書く**
        if (next[m.group] !== groupNames[m.group]) {
          groupNames = next;
          save(KEY.groupNames, groupNames);
        }
      }
      render();
      break;
    case "channel_created": {
      channelNames = rememberChannelName(channelNames, m.room, m.display_name);
      save(KEY.channels, channelNames);
      // **新しいグループを作った応答**なら、採番されたコードを所属として覚える
      // （サーバは所属を知らない＝ADR-0019 §1-7。名前も返らないので打った名前を使う）
      const code = parseRoomCode(m.room).group;
      if (pendingGroupName !== "" && code !== "" && !groups.includes(code)) {
        groups = addGroup(groups, code);
        save(KEY.groups, groups);
        groupNames = rememberChannelName(groupNames, code, pendingGroupName);
        save(KEY.groupNames, groupNames);
        group = code;
        log("info", `グループを作りました: ${pendingGroupName}（${code}）`);
        // **作った直後に招待 URL を出す**（2026-10-29 のユーザー判断）。この直後に
        // 作ったチャンネルへ入室するので、**出す先は通話中の画面の URL 欄**になる。
        // ボタンは増やさない決定なので、**何の URL かを文字で添える**
        const url = groupInviteUrl(code);
        screen.setInviteUrl(url, "グループの招待 URL");
        log("info", `グループの招待 URL: ${url}`);
      }
      pendingGroupName = "";
      screen.createDone();
      log("info", `チャンネルを作りました: ${m.display_name}`);
      void join(m.room);
      break;
    }
    case "joined": {
      c.self = m.participant_id;
      c.profile = m.audio_profile;
      c.roster = new Map(m.roster.map((p) => [p.id, p]));
      phase = { kind: "in-call" };
      pendingAction = null;
      reconnect.settled();
      rememberRecent(c.room);
      screen.setCallTitle(channelNames[c.room] ?? parseRoomCode(c.room).channel);
      screen.setInviteUrl(inviteUrl(c.room));
      screen.show("call");
      log("info", `入室しました: 自分は #${m.participant_id} / 参加者 ${m.roster.length} 人`);
      void startAudio(m.audio_profile);
      void keepAwake.acquire();
      render();
      break;
    }
    case "roster_update": {
      for (const p of m.joined ?? []) c.roster.set(p.id, p);
      for (const p of m.changed ?? []) c.roster.set(p.id, p);
      for (const id of m.left ?? []) {
        c.roster.delete(id);
        personGainDb.delete(id);
      }
      // **入退室の知らせ**（行として出す。音は鳴らさない。画面仕様 §4）
      const joined = m.joined ?? [];
      const left = m.left ?? [];
      if (joined.length > 0) {
        screen.setLogLine(`${joined.map((p) => p.name).join("、")} が入室しました（${c.roster.size} 人）`);
      } else if (left.length > 0) {
        screen.setLogLine(`退室しました（${c.roster.size} 人）`);
      }
      render();
      break;
    }
    case "speaking":
      // **毎回「全員」が入る**ので丸ごと置き換える（自分も入る）
      c.speaking = new Set(m.ids);
      render();
      break;
    case "error": {
      log("error", `サーバが拒否しました【${m.code}】${m.message}`);
      const asked = pendingAction;
      pendingGroupName = "";
      pendingAction = null;
      // **作ろうとした直後の「不正な要求」は招待 URL の話ではない**ので、文言を取り違えない
      if (m.code === "invalid_request" && (asked === "group" || asked === "channel")) {
        const what = asked === "group" ? "グループ" : "チャンネル";
        showNotice({ head: `${what}を作れませんでした`, action: "もう一度試してください" });
      } else {
        showNotice(rejectionNotice(m.code));
      }
      // **画面を止めるのは「このままでは何もできない」ときだけ。**
      // バージョン違いは読み込み直すまで何もできず、無い招待 URL は入室の行き先が無い。
      // **満員・上限・作成の失敗では止めない** — サーバは接続を閉じないので、
      // 別のチャンネルを選ぶ・作り直すことができる（`session.rs` の `close: false`）
      if (m.code === "unsupported_version" || (m.code === "invalid_request" && asked === "join")) {
        phase = { kind: "stopped" };
      }
      render();
      break;
    }
    case "uplink_report":
      break;
  }
}

/** 前にいた場所として覚える（新しい順。板 #220）。 */
function rememberRecent(room: string): void {
  if (room === "") return;
  recent = [room, ...recent.filter((r) => r !== room)].slice(0, RECENT_MAX);
  save(KEY.recent, recent);
}

function showNotice(notice: { head: string; action?: string; detail?: boolean }): void {
  screen.setNotice(notice, notice.detail ? detailLine(APP_VERSION, new Date()) : "");
}

// ---- 音声 ------------------------------------------------------------------

/** 入室応答の設定でマイクと Worklet を用意する（**設定が分かってからでないと作れない**）。 */
async function startAudio(profile: AudioProfile): Promise<void> {
  const c = call;
  if (!c || c.audio) return;
  try {
    const audio = await Audio.start(profile, {
      onFrame: (bytes) => c.transport.sendFrame(bytes),
      onError: (where, message) => log("error", `音声側の異常【${where}】${message}`),
      onStats: (st) => {
        // **自分が喋っていることはコアでも分かる**ので、サーバの `speaking` が
        // 来ないあいだも自分の行だけは光らせる（画面仕様 §3）
        if (c.self !== null && !audio.isMuted && !audio.listenOnly) {
          if (st.speaking) c.speaking.add(c.self);
          else c.speaking.delete(c.self);
        }
        // ゲージは 3 つ出す（画面仕様 §6-1）— いまの入力・静かなときの音量・効いている境目。
        // **平滑化はコアでやってある**ので、ここでは渡すだけ（タスク #178）
        screen.setGauge(st.noiseFloorDb, st.vadThresholdDb, st.inputLevelDb);
        render();
      },
    });
    if (!call) {
      await audio.stop(); // 準備中に退室した
      return;
    }
    c.audio = audio;
    if (c.self !== null) audio.setParticipant(c.self);
    audio.setGainDb(audioPrefs.send);
    audio.setMasterGainDb(audioPrefs.master);
    const maxLowpass = autoLowpassHz(profile);
    const lowpass = Math.min(audioPrefs.lowpass ?? maxLowpass, maxLowpass);
    audio.setHighpassHz(audioPrefs.highpass);
    audio.setLowpassHz(lowpass);
    // 無送信はチャンネルの設定に従う（画面には出さない。画面仕様 §6-2）
    audio.setDtx(profile.dtx);
    screen.setBand(audioPrefs.highpass, lowpass, maxLowpass);
    screen.setGains(audioPrefs.master, audioPrefs.send);
    screen.setMuted(false);
    for (const [id, db] of personGainDb) c.transport.sendSetMixGain(id, db);
    if (audio.listenOnly) {
      // **文言は 2 文だけ**（「聞くだけ」とは書かない。自分の行のミュートの印で伝える）
      showNotice({ head: "マイクが認識できません", action: "設定を確認してください" });
      log("warn", `マイクが使えません: ${audio.micError ?? "理由不明"}`);
    }
    log("info", `音声の用意ができました（ブラウザ内部の遅延 ${audio.baseLatencyMs} ms）`);
    log("info", `マイク: ${audio.micDescription}`);
    render();
  } catch (e) {
    log("error", `マイク・音声処理を用意できませんでした: ${String(e)}`);
    showNotice({ head: "マイクを使えませんでした", action: "設定を確認してください" });
    void leave();
  }
}

// ---- 切れたときと退室 ------------------------------------------------------

function onClosed(reason: string): void {
  if (leavingByUs) {
    log("info", "退室しました");
    return;
  }
  const c = call;
  log("warn", `接続が切れました: ${reason}`);
  const uptimeMs = c ? Date.now() - c.startedAt : 0;
  void teardown();
  planReconnect("transport", uptimeMs);
}

/** 張り直しの一手を決めて待つ（方針はコアと同じ `reconnect.ts`。画面仕様 §7-1）。 */
function planReconnect(cause: "transport" | "attempt" | "unsupported" | "rejected" | "by-us", uptimeMs: number): void {
  const plan = reconnect.plan(cause, uptimeMs);
  if (plan.kind === "retry") {
    phase = { kind: "reconnecting", attempt: plan.attempt, max: POLICY.maxAttempts };
    rejoinCount += 1;
    reconnectTimer = window.setTimeout(() => {
      reconnectTimer = null;
      void connect();
    }, plan.delayMs);
  } else if (plan.kind === "give-up") {
    // **自動では何もしない**（「繋ぎ直す」を押してもらう）
    phase = { kind: "gave-up" };
  } else {
    phase = { kind: "stopped" };
  }
  render();
}

/** 待っている張り直しを取り消す（利用者が退室・繋ぎ直すを押したとき）。 */
function cancelReconnect(): void {
  if (reconnectTimer !== null) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

/** 接続と音声を片付ける（画面は呼び出し側が決める）。 */
async function teardown(): Promise<void> {
  const c = call;
  call = null;
  rooms = [];
  if (!c) return;
  await c.audio?.stop();
  personGainDb.clear();
  await keepAwake.release();
}

async function leave(): Promise<void> {
  const c = call;
  cancelReconnect();
  if (!c) {
    phase = { kind: "connecting" };
    screen.show("home");
    render();
    void connect();
    return;
  }
  leavingByUs = true;
  try {
    await c.transport.leave();
  } catch {
    // 閉じている途中なら気にしない
  } finally {
    await teardown();
    leavingByUs = false;
    reconnect.settled();
    rejoinCount = 0;
    screen.setLogLine("");
    // **退室で招待 URL の表示を消す。** 残すと、次に別のチャンネルへ入ったときに
    // 前の行き先（グループを作った直後ならグループ宛）が出たままになる
    screen.setInviteUrl("");
    screen.show("home");
    phase = { kind: "connecting" };
    render();
    // 退室したら一覧に戻るので、繋ぎ直して一覧を取り直す
    void connect();
  }
}

// ---- 招待 URL とログ -------------------------------------------------------

function inviteUrl(room: string): string {
  const { group: g, channel } = parseRoomCode(room);
  return buildInviteUrl(location.origin + location.pathname, { group: g, channel });
}

/** いま選んでいるグループの招待 URL（`#group=<コード>`。2026-10-29）。 */
function groupInviteUrl(code: string): string {
  return buildGroupInviteUrl(location.origin + location.pathname, code);
}

/**
 * **グループの招待 URL をコピーする**（2026-10-29）。
 *
 * チャンネル宛（{@link copyInvite}）と違い、**入室前に押せる**。手応えの出し方は同じ
 * （失敗する端末があるので URL も画面に出す。画面仕様 §3）。
 */
async function copyGroupInvite(): Promise<void> {
  if (group === "") return;
  const url = groupInviteUrl(group);
  screen.setGroupInviteUrl(url);
  try {
    // **ボタンの `click` の中から呼ぶ**（iOS Safari は操作が起点でないと断る）
    await navigator.clipboard.writeText(url);
    screen.setGroupCopyFeedback("コピーしました");
  } catch {
    screen.setGroupCopyFeedback("コピーできません", true);
  }
  window.setTimeout(() => screen.setGroupCopyFeedback(""), 4000);
}

/** 招待 URL をコピーする。**押した手応えを出す**（失敗する端末がある。画面仕様 §3）。 */
async function copyInvite(): Promise<void> {
  const room = call?.room || selected;
  if (room === "") return;
  const url = inviteUrl(room);
  screen.setInviteUrl(url);
  try {
    // **ボタンの `click` の中から呼ぶ**（iOS Safari は操作が起点でないと断る）
    await navigator.clipboard.writeText(url);
    screen.setCopyFeedback("コピーしました");
  } catch {
    screen.setCopyFeedback("コピーできません", true);
  }
  window.setTimeout(() => screen.setCopyFeedback(""), 4000);
}

/** ログをテキストにして保存する（**サーバには送らない**。板 #82 でリリース前に外す）。 */
function saveLog(): void {
  const now = new Date();
  const meta: LogMeta = [
    ["ページ", location.href],
    ["ブラウザ", navigator.userAgent],
    ["接続先", SERVER_URL],
    ["バージョン", APP_VERSION],
  ];
  if (call?.room) meta.push(["チャンネル", call.room]);
  const blob = new Blob([buildLogText(now, meta, recordedLines)], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = logFileName(now);
  a.rel = "noopener";
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

// ---- 起動 ------------------------------------------------------------------

// **招待 URL を開いたら、そのグループを所属に加える**（ADR-0019 §1-7）。
// これが無いと一覧が返らず、入室前に表示名も在室者も出せない（板 #197 の回答）。
// **チャンネルまで指すもの（`#room=`）とグループだけのもの（`#group=`）がある**
// （2026-10-29。優先順は `invite.ts` の parseInviteTarget が持つ）
const target = parseInviteTarget(location.hash);
if (target) {
  groups = addGroup(groups, target.group);
  save(KEY.groups, groups);
  group = target.group;
  // **グループだけの招待では行き先を選ばない** — どのチャンネルに入るかは受け取った人が決める
  if (target.kind === "channel") {
    invitedRoom = toRoomId(target);
    selected = invitedRoom;
  }
}

screen.setName(myName);
screen.setVersionLine(`version ${APP_VERSION} ／ 接続先 ${location.hostname}`);
screen.setBand(audioPrefs.highpass, audioPrefs.lowpass ?? 3400, 3400);
screen.setGains(audioPrefs.master, audioPrefs.send);
render();

// **検証の欄**（設定の画面の中。板 #90 で外す）
mountDiagnostics(screen.diagnostics, {
  log,
  getUrl: () => SERVER_URL,
  isInCall: () => Boolean(call?.audio),
});

// 前回の起動で送れなかった到達確認を送る（ADR-0014 §5-2）
void flushQueued();

document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") void keepAwake.onVisible();
});
window.addEventListener("pagehide", () => void leave());

if (typeof WebTransport === "undefined") {
  // **フォールバックは本番に入っていない**（ADR-0018・V1 の測り直し待ち）
  showNotice({ head: "このブラウザでは通話を使えません", action: `${SUPPORTED_BROWSERS} でお試しください` });
  phase = { kind: "stopped" };
  render();
} else {
  void connect();
}
